import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { Bot } from 'lucide-react';
import {
  listMcpOAuthSessions,
  revokeMcpOAuthSession,
  toMcpApiErrorMessage,
  type McpOAuthSession
} from '../api/mcpOAuthApi';
import {
  detectMcpClientBrand,
  McpClientBrandMark
} from './McpClientBrand';

function formatWhen(createdAt: number): string {
  try {
    return new Date(createdAt).toLocaleString('fa-IR');
  } catch {
    return '';
  }
}

export function McpSessionsCard() {
  const [sessions, setSessions] = useState<McpOAuthSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setSessions(await listMcpOAuthSessions());
    } catch (caught: unknown) {
      setSessions([]);
      setError(toMcpApiErrorMessage(caught, 'خواندن نشست‌های MCP ناموفق بود'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (session: McpOAuthSession) => {
    setRevokingId(session.id);
    setError(null);
    try {
      await revokeMcpOAuthSession(session.id);
      setSessions(current =>
        (current ?? []).filter(item => item.id !== session.id)
      );
    } catch (caught: unknown) {
      setError(toMcpApiErrorMessage(caught, 'لغو این نشست ناموفق بود'));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Card className="h-100 mb-0 shadow-sm">
      <Card.Header className="bg-body-tertiary border-bottom py-3">
        <h5 className="mb-0">نشست‌های متصل</h5>
      </Card.Header>
      <Card.Body className="p-3">
        <p className="fs--1 text-body-secondary mb-3">
          هر ردیف یک اتصال فعال است که با تأیید شما به حساب میزفا تولز متصل شده
          است. برای قطع دسترسی، اتصال موردنظر را لغو کنید.
        </p>

        {error && (
          <Alert variant="danger" className="fs--1 py-2">
            {error}
          </Alert>
        )}

        {sessions === null && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {sessions?.length === 0 && (
          <p className="fs--1 mb-0">
            نشست فعالی ثبت نشده است. اتصال‌هایی که قبل از این به‌روزرسانی ساخته
            شده‌اند اینجا دیده نمی‌شوند؛ برای قطع آن‌ها کلاینت را یک‌بار جدا
            کنید و دوباره وصل شوید.
          </p>
        )}

        {sessions && sessions.length > 0 && (
          <div className="d-flex flex-column gap-2">
            {sessions.map(session => {
              const clientBrand = detectMcpClientBrand(
                session.claimedName,
                session.redirectUri
              );
              return (
                <div
                  key={session.id}
                  className="border rounded-3 px-3 py-3 d-flex flex-column flex-md-row gap-3 align-items-md-center"
                >
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span
                        className="d-inline-flex align-items-center justify-content-center rounded-2 border bg-body-tertiary flex-shrink-0"
                        style={{ width: 36, height: 36 }}
                      >
                        {clientBrand ? (
                          <McpClientBrandMark brand={clientBrand} size={21} />
                        ) : (
                          <Bot size={19} className="text-primary" aria-hidden />
                        )}
                      </span>
                      <span className="fw-semi-bold">
                        {session.claimedName || 'کلاینت MCP'}
                      </span>
                    </div>
                    <div className="fs--2 text-600 mt-1">
                      {formatWhen(session.createdAt)}
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="flex-shrink-0"
                    disabled={revokingId === session.id}
                    onClick={() => void revoke(session)}
                  >
                    {revokingId === session.id ? 'در حال لغو' : 'لغو دسترسی'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
